import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { IAiScriptExplanationChatSource } from "@/payroll/ai-script/types";
import { CollapsibleSection } from "./collapsible-section";

const getSourceTitle = (source: IAiScriptExplanationChatSource) =>
  source.label ?? source.title ?? source.type ?? "Source";

const getSourceDetail = (source: IAiScriptExplanationChatSource) =>
  source.detail ?? source.description ?? null;

// Deep link into ServiceTitan for record-backed sources; the invoice is the
// more specific record when both ids are present.
const getServiceTitanLink = (source: IAiScriptExplanationChatSource) => {
  if (source.invoice_id) {
    return {
      url: `/#/Invoice/${source.invoice_id}`,
      label: `Open invoice ${source.invoice_id} in ServiceTitan`,
    };
  }
  if (source.job_id) {
    return {
      url: `/#/Job/Index/${source.job_id}`,
      label: `Open job ${source.job_id} in ServiceTitan`,
    };
  }

  return null;
};

export const MessageSources = ({
  sources,
}: {
  sources: IAiScriptExplanationChatSource[];
}) => (
  <CollapsibleSection label={`Sources (${sources.length})`}>
    <Stack gap="sm">
      {sources.map((source, index) => {
        const detail = getSourceDetail(source);
        const serviceTitanLink = getServiceTitanLink(source);

        return (
          <Stack
            key={source.id ?? `${getSourceTitle(source)}-${index}`}
            gap="none"
          >
            <span className="inline-flex items-center">
              <Typography variant="captionW1SemiBold" textColor="secondary">
                {getSourceTitle(source)}
              </Typography>
              {serviceTitanLink && (
                <ServiceTitanIconLink
                  url={serviceTitanLink.url}
                  label={serviceTitanLink.label}
                />
              )}
            </span>
            {detail && (
              <Typography variant="captionW1" textColor="tertiary">
                {detail}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  </CollapsibleSection>
);
